import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';

import { debounce } from '@codex/helpers';

import { searchSuggestions } from './helpers/locationSearchHelpers';
import LocationSearchInput from './presentionalComponents/LocationSearchInput';
import LocationsList from './presentionalComponents/LocationsList';
import WrongLocationInputMessage from './presentionalComponents/WrongLocationInputMessage';
import { getTranslation } from '../../translations/translations';

import { resolveLocationAndSearchDeliveries } from '../../reduck/listingPageLocationSearchSetup.reduck';
import { getActiveListingPage } from '../../reduck/selectors';

const Wrapper = styled.div`
  width: 100%;
  min-width: 200px;
  position: relative;
`;

class LocationSearchInputBlock extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isTextInputFocused: false,
      containsTextInputTwoOrMoreCharacter: false,
      foundSuggestions: null,
      isSuggestionsLoading: false,
      hoveredLocation: null
    };

    this.debouncedOnTextInputChange = debounce(this.onTextInputChange.bind(this), 500);
    this.setFoundSuggestions = this.setFoundSuggestions.bind(this);
    this.setHoveredLocation = this.setHoveredLocation.bind(this);
  }

  async onTextInputChange(textInputValue) {
    if (textInputValue.length > 1) {
      this.setContainsTextInputTwoOrMoreCharacter(true);
      this.setIsSuggestionsLoading(true);

      const suggestions = await searchSuggestions(textInputValue);

      if (suggestions.length > 0) {
        this.setFoundSuggestions(suggestions);

        if (suggestions[0].location.toLowerCase().startsWith(textInputValue.toLowerCase().trim())) {
          this.setHoveredLocation({
            // clone is here used in order not to change original locations from response which are stored in local storage
            ...suggestions[0],
            hoveredByDefault: true
          });
        } else {
          this.setHoveredLocation(null);
        }
      } else {
        this.setFoundSuggestions(null);
        this.setHoveredLocation(null);
      }

      this.setIsSuggestionsLoading(false);
    } else if (textInputValue.length === 0) {
      this.setContainsTextInputTwoOrMoreCharacter(false);
      this.setFoundSuggestions(null);
    } else {
      this.setContainsTextInputTwoOrMoreCharacter(false);
    }
  }

  setIsTextInputFocused(isFocused) {
    this.setState({
      isTextInputFocused: isFocused
    });
  }

  setContainsTextInputTwoOrMoreCharacter(contains) {
    this.setState({
      containsTextInputTwoOrMoreCharacter: contains
    });
  }

  setFoundSuggestions(newSuggestions) {
    this.setState({
      foundSuggestions: newSuggestions,
      hoveredLocation: null
    });
  }

  setHoveredLocation(newLocation) {
    this.setState({
      hoveredLocation: newLocation
    });
  }

  setIsSuggestionsLoading(isSuggestionsLoading) {
    this.setState({
      isSuggestionsLoading
    });
  }

  renderSuggestionsBlock() {
    const { previouslyResolvedLocations } = this.props;
    const {
      foundSuggestions,
      containsTextInputTwoOrMoreCharacter,
      isSuggestionsLoading
    } = this.state;

    if (foundSuggestions) {
      return (
        <LocationsList
          locations={foundSuggestions}
          onListItemSelected={(selectedLocation) => {
            this.props.onLocationSelected(
              selectedLocation,
              this.props.currentActiveProductIds,
              this.props.currentActivePageUrlSuffix
            );
            this.setHoveredLocation(null);
            this.setIsTextInputFocused(false);
          }}
          onListItemHovered={(hoveredLocation) => {
            this.setHoveredLocation(hoveredLocation);
          }}
        />
      );
    }
    if (!containsTextInputTwoOrMoreCharacter && previouslyResolvedLocations.length > 0) {
      return (
        <LocationsList
          headerLabel={getTranslation('previousSearchesLabel')}
          locations={previouslyResolvedLocations}
          onListItemSelected={(selectedLocation) => {
            this.props.onLocationSelected(
              selectedLocation,
              this.props.currentActiveProductIds,
              this.props.currentActivePageUrlSuffix
            );
            this.setHoveredLocation(null);
            this.setIsTextInputFocused(false);
          }}
          onListItemHovered={(hoveredLocation) => {
            this.setHoveredLocation(hoveredLocation);
          }}
        />
      );
    }
    if (containsTextInputTwoOrMoreCharacter && !isSuggestionsLoading) {
      return <WrongLocationInputMessage message={getTranslation('wrongLocationInputMessage')} />;
    }

    return null;
  }

  render() {
    const { searchLocationPending, pendingProductsRequest, name, isMobile } = this.props;
    const {
      hoveredLocation,
      isTextInputFocused,
      containsTextInputTwoOrMoreCharacter,
      foundSuggestions
    } = this.state;

    let searchInputState;

    if (!foundSuggestions && isTextInputFocused && containsTextInputTwoOrMoreCharacter) {
      searchInputState = 'invalid';
    } else if (hoveredLocation) {
      searchInputState = 'valid';
    } else {
      searchInputState = 'neutral';
    }

    return (
      <Wrapper className={'dc-location-search-input-block'}>
        <LocationSearchInput
          name={name}
          placeholderLabel={getTranslation('postcodeCheckerPlaceholder')}
          loadingLabel={getTranslation('postcodeCheckerPending')}
          disabled={searchLocationPending || pendingProductsRequest}
          loading={searchLocationPending}
          onTextInputBlur={() => {
            this.setHoveredLocation(null);
            this.setIsTextInputFocused(false);
          }}
          onTextInputChange={this.debouncedOnTextInputChange}
          onTextInputFocus={() => {
            this.setIsTextInputFocused(true);
          }}
          onClearButtonClick={() => {
            this.setContainsTextInputTwoOrMoreCharacter(false);
            this.setFoundSuggestions(null);
          }}
          onEscKeyClick={() => {
            this.setContainsTextInputTwoOrMoreCharacter(false);
            this.setFoundSuggestions(null);
          }}
          onSearchButtonClick={() => {
            this.props.onLocationSelected(
              this.state.hoveredLocation,
              this.props.currentActiveProductIds,
              this.props.currentActivePageUrlSuffix
            );
            this.setHoveredLocation(null);
            this.setIsTextInputFocused(false);
          }}
          highlightedInputTextSuffix={!isMobile && hoveredLocation ? hoveredLocation.location : ''}
          searchInputState={searchInputState}
        />
        {isTextInputFocused &&
          !searchLocationPending &&
          !pendingProductsRequest &&
          this.renderSuggestionsBlock()}
      </Wrapper>
    );
  }
}

LocationSearchInputBlock.propTypes = {
  // DON'T CHANGE THE VALUES of the following prop, the format is due to analytics
  name: PropTypes.oneOf(['above product listing', 'filter block']).isRequired,
  searchLocationPending: PropTypes.bool.isRequired,
  pendingProductsRequest: PropTypes.bool.isRequired,
  previouslyResolvedLocations: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      retailLocation: PropTypes.bool
    })
  ).isRequired,
  isMobile: PropTypes.bool.isRequired,
  onLocationSelected: PropTypes.func.isRequired,
  currentActiveProductIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentActivePageUrlSuffix: PropTypes.string.isRequired
};

function mapStateToProps(state) {
  return {
    // only max 5 previous locations are shown in the component
    previouslyResolvedLocations: state.listingPage.locationSearchSetup.previouslyResolvedLocations.slice(
      0,
      5
    ),
    isMobile: state.listingPage.browser
      ? state.listingPage.browser.device.deviceType === 'mobile'
      : false,
    currentActiveProductIds: getActiveListingPage(state).productList.map((product) => product.id),
    currentActivePageUrlSuffix: getActiveListingPage(state).pageUrlSuffix
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onLocationSelected: (location, currentActiveProductIds, currentActivePageUrlSuffix) => {
      dispatch(
        resolveLocationAndSearchDeliveries(
          location,
          currentActiveProductIds,
          currentActivePageUrlSuffix
        )
      );
    }
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(LocationSearchInputBlock);
